import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStripe, useElements } from '@stripe/react-stripe-js';
import { paymentService } from '../lib/paymentService';
import { useCart } from '../contexts/CartContext';

export function usePayment() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [paymentIntent, setPaymentIntent] = useState(null);
  const [orderId, setOrderId] = useState(null);
  
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const { cartItems, clearCart } = useCart();

  // Create payment intent
  const createPaymentIntent = useCallback(async (orderData) => {
    try {
      setLoading(true);
      setError('');
      
      console.log('[usePayment] createPaymentIntent called with orderData:', {
        amount: orderData.amount,
        currency: orderData.currency,
        itemsCount: orderData.items?.length,
        total: orderData.total
      });
      
      const response = await paymentService.createPaymentIntent(orderData);
      
      console.log('[usePayment] createPaymentIntent response:', {
        hasResponse: !!response,
        hasPaymentIntent: !!response.paymentIntent,
        success: response.success,
        responseKeys: response ? Object.keys(response) : []
      });
      
      if (!response || !response.paymentIntent) {
        throw new Error('Invalid response from payment service: missing paymentIntent');
      }
      
      const intent = response.paymentIntent;
      
      // Validate required fields
      if (!intent.client_secret) {
        throw new Error('Payment intent missing client_secret');
      }
      
      if (!intent.id) {
        throw new Error('Payment intent missing id');
      }
      
      console.log('[usePayment] Payment intent created successfully:', {
        id: intent.id,
        hasClientSecret: !!intent.client_secret,
        status: intent.status,
        amount: intent.amount
      });
      
      setPaymentIntent(intent);
      
      return intent;
    } catch (err) {
      console.error('[usePayment] createPaymentIntent error:', err);
      setError(err.message || 'Failed to create payment intent');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Process payment
  const processPayment = useCallback(async (paymentMethodId, billingDetails = {}, paymentIntentParam = null) => {
    // Log ALL arguments received (not using arguments object since it's an arrow function)
    // But we can log what we explicitly received
    console.log('[usePayment] processPayment CALLED - ALL parameters received:', {
      arg0_paymentMethodId: paymentMethodId,
      arg1_billingDetails: billingDetails,
      arg2_paymentIntentParam: paymentIntentParam,
      paymentIntentParamType: typeof paymentIntentParam,
      paymentIntentParamIsNull: paymentIntentParam === null,
      paymentIntentParamIsUndefined: paymentIntentParam === undefined,
      paymentIntentParamIsObject: typeof paymentIntentParam === 'object',
      paymentIntentParamKeys: paymentIntentParam && typeof paymentIntentParam === 'object' ? Object.keys(paymentIntentParam) : 'not an object',
      hasPaymentIntent: !!paymentIntent
    });
    
    const intentToUse = paymentIntentParam || paymentIntent;
    
    console.log('[usePayment] processPayment called with:', {
      hasPaymentIntentParam: !!paymentIntentParam,
      paymentIntentParamType: paymentIntentParam ? typeof paymentIntentParam : 'null',
      paymentIntentParamKeys: paymentIntentParam ? Object.keys(paymentIntentParam) : [],
      paymentIntentParamId: paymentIntentParam?.id,
      paymentIntentParamStatus: paymentIntentParam?.status,
      hasPaymentIntent: !!paymentIntent,
      intentToUse: intentToUse ? {
        id: intentToUse.id,
        hasClientSecret: !!intentToUse.client_secret,
        clientSecretPrefix: intentToUse.client_secret ? intentToUse.client_secret.substring(0, 20) + '...' : null,
        status: intentToUse.status,
        intentKeys: Object.keys(intentToUse)
      } : null,
      stripeReady: !!stripe,
      elementsReady: !!elements
    });
    
    if (!stripe || !elements || !intentToUse) {
      const errorMsg = !stripe ? 'Stripe not ready' : !elements ? 'Card elements not ready' : 'Payment intent not available';
      console.error('[usePayment] Payment system not ready:', errorMsg);
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }

    // Validate that we have a client_secret (unless payment is already succeeded/processing)
    // Confirmed/succeeded payment intents from digital wallets might not need client_secret for order creation
    if (!intentToUse.client_secret && intentToUse.status !== 'succeeded' && intentToUse.status !== 'processing') {
      const errorMsg = 'Payment intent missing client_secret. Please try again.';
      console.error('[usePayment] Missing client_secret in payment intent:', {
        id: intentToUse.id,
        status: intentToUse.status,
        hasClientSecret: !!intentToUse.client_secret,
        intentKeys: Object.keys(intentToUse)
      });
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
    
    // If payment is already succeeded/processing, we can proceed without client_secret
    if (intentToUse.status === 'succeeded' || intentToUse.status === 'processing') {
      console.log('[usePayment] Payment already succeeded/processing, skipping client_secret validation');
    }

    try {
      setLoading(true);
      setError('');

      // Check if this is a mock payment intent (for testing)
      if (intentToUse.id && intentToUse.id.startsWith('pi_mock_')) {
        console.log('Processing mock payment intent:', intentToUse.id);
        
        // For mock payments, simulate success without calling Stripe
        const mockOrderData = {
          paymentIntentId: intentToUse.id,
          items: cartItems.map(item => ({
            ...item,
            fulfillmentDay: item.fulfillmentDay || null // Ensure fulfillment day is included
          })),
          total: intentToUse.amount,
          status: 'paid',
          billingDetails,
        };

        const order = await paymentService.createOrder(mockOrderData);
        setOrderId(order.id);
        
        // Update inventory for ordered products (mock payments)
        try {
          console.log('[Payment] Attempting to import inventoryApi...');
          const { updateInventoryAfterOrder } = await import('../utils/inventoryApi');
          console.log('[Payment] Successfully imported updateInventoryAfterOrder:', typeof updateInventoryAfterOrder);
          
          // Use the processed order items that include fulfillment days
          const orderItems = mockOrderData.items;
          console.log('[Payment] Calling updateInventoryAfterOrder with items:', orderItems);
          await updateInventoryAfterOrder(orderItems, null); // Pass null for serviceDate since we're using individual fulfillment days
          console.log('[Payment] Inventory updated successfully for mock order:', order.id);
        } catch (inventoryError) {
          console.error('[Payment] Failed to update inventory for mock order:', inventoryError);
          // Don't fail the payment if inventory update fails
          // The order is still valid, just log the error
        }
        
        // Clear cart after successful order
        console.log('[Payment] Clearing cart after successful mock order');
        clearCart();
        
        return { success: true, orderId: order.id };
      }

      // CRITICAL: Validate inventory BEFORE confirming payment
      // This prevents charging the customer if inventory is insufficient
      console.log('[usePayment] Validating inventory before payment confirmation...');
      try {
        const { validateInventoryAvailability } = await import('../utils/inventoryApi');
        
        const orderItemsForValidation = cartItems.map(item => ({
          ...item,
          productId: item.productId || item.id,
          // Try multiple possible date field names
          fulfillmentDay: item.fulfillmentDay || item.pickupDate || item.serviceDate || null,
          pickupDate: item.pickupDate || item.fulfillmentDay || item.serviceDate || null,
          serviceDate: item.serviceDate || item.fulfillmentDay || item.pickupDate || null
        }));
        
        console.log('[usePayment] Cart items for validation:', orderItemsForValidation.map(item => ({
          id: item.id,
          productId: item.productId,
          fulfillmentDay: item.fulfillmentDay,
          pickupDate: item.pickupDate,
          serviceDate: item.serviceDate,
          quantity: item.qty,
          name: item.name
        })));
        
        const validation = await validateInventoryAvailability(orderItemsForValidation);
        if (!validation.valid) {
          const errorMsg = `Inventory validation failed: ${validation.errors.join(', ')}`;
          console.error('[usePayment] ❌ Inventory validation FAILED - BLOCKING payment:', {
            errors: validation.errors,
            cartItems: cartItems.length,
            validationItems: orderItemsForValidation
          });
          setError(errorMsg);
          // CRITICAL: Return early to prevent payment confirmation
          // Do NOT proceed to stripe.confirmCardPayment
          return { success: false, error: errorMsg };
        }
        console.log('[usePayment] ✅ Inventory validation passed, proceeding with payment confirmation');
      } catch (validationError) {
        console.error('[usePayment] Inventory validation error:', validationError);
        const errorMsg = validationError.message || 'Failed to validate inventory. Please try again.';
        setError(errorMsg);
        return { success: false, error: errorMsg };
      }

      // Check if payment is already confirmed/succeeded (e.g., from digital wallet)
      let confirmedIntent;
      if (intentToUse.status === 'succeeded' || intentToUse.status === 'processing') {
        console.log('[usePayment] Payment intent already succeeded/processing, skipping confirmation:', {
          id: intentToUse.id,
          status: intentToUse.status,
          passedAsParam: !!paymentIntentParam,
          hasClientSecret: !!intentToUse.client_secret
        });
        confirmedIntent = intentToUse;
      } else {
        // Original Stripe confirmation code for real payments
        console.log('[usePayment] Confirming payment with Stripe:', {
          clientSecret: intentToUse.client_secret.substring(0, 20) + '...',
          paymentMethodId,
          paymentIntentId: intentToUse.id,
          currentStatus: intentToUse.status
        });
        
        const { error: stripeError, paymentIntent: confirmedPaymentIntent } = await stripe.confirmCardPayment(
          intentToUse.client_secret,
          {
            payment_method: paymentMethodId,
          }
        );

        if (stripeError) {
          console.error('[usePayment] Stripe confirmation error:', {
            code: stripeError.code,
            message: stripeError.message,
            type: stripeError.type,
            paymentIntent: stripeError.payment_intent
          });
          
          // Check if the error is because payment was already confirmed
          if (stripeError.payment_intent?.status === 'succeeded') {
            console.log('[usePayment] Payment was already confirmed, using existing intent');
            confirmedIntent = stripeError.payment_intent;
          } else {
            setError(stripeError.message || 'Payment failed');
            return { success: false, error: stripeError.message };
          }
        } else {
          confirmedIntent = confirmedPaymentIntent;
        }
      }

      if (confirmedIntent.status === 'succeeded') {
        // Create order in our system
        const orderData = {
          paymentIntentId: confirmedIntent.id,
          items: cartItems.map(item => ({
            ...item,
            fulfillmentDay: item.fulfillmentDay || null // Ensure fulfillment day is included
          })),
          total: confirmedIntent.amount,
          status: 'confirmed',
          billingDetails,
        };

        const order = await paymentService.createOrder(orderData);
        console.log('[Payment] Order created:', order);
        console.log('[Payment] Order ID:', order.id);
        setOrderId(order.id);
        
        // Clear cart after successful order
        console.log('[Payment] Clearing cart after successful real order');
        clearCart();
        
        console.log('[Payment] Returning success with orderId:', order.id);
        return { success: true, orderId: order.id };
      } else {
        setError('Payment was not successful');
        return { success: false, error: 'Payment was not successful' };
      }
    } catch (err) {
      setError(err.message || 'Payment processing failed');
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, [stripe, elements, paymentIntent, cartItems, clearCart]);

  // Handle payment success
  const handlePaymentSuccess = useCallback((orderId) => {
    navigate(`/order-confirmation/${orderId}`);
  }, [navigate]);

  // Reset payment state
  const resetPayment = useCallback(() => {
    setPaymentIntent(null);
    setOrderId(null);
    setError('');
  }, []);

  return {
    loading,
    error,
    paymentIntent,
    orderId,
    createPaymentIntent,
    processPayment,
    handlePaymentSuccess,
    resetPayment,
  };
}
